// Quote Builder — right pane: quote details, zones, line items, delivery.
//
// Split into three mounts so the picker can sit between meta and items
// (source kos-qb-v3.js:1286-1317 right-pane order):
//   1. mountQuoteMeta  → renderQuoteMeta (customer / project / VAT / etc)
//   2. (picker-area)   → renderPicker — mounted by picker-area.ts
//   3. mountQuoteItems → renderItemsArea + delivery
//
// Source: kos-qb-v3.js:1441 (renderQuoteMeta), 3281-3500 (renderItemsArea).

import { confirmDialog, promptDialog, createModal } from "@tkos/shared-ui";
import type { QuoteStore } from "./state";
import type { PageContext } from "../../shell/types";
import type { GroupBy, ItemCategory, QuoteItem, QuoteZone } from "./types";
import {
  renderWhyButton, renderEditor as renderWhyEditor, toggleEditor as toggleWhyEditor,
  closeEditor as closeWhyEditor, isEditorOpen as isWhyOpen, dismissNudge, markAnomaly,
  isAnomalousEdit, isNudged, wasPrompted, saveRationale, loadForQuote,
} from "./rationale";
import { escapeHtml, fmtPence, newId, pencify, svgIcon } from "./formatters";
import { recalcItem } from "./pricing";
import { mountCustomerTypeahead } from "./customer-typeahead";
import { mountAddItemSearch, type AddItemSearchHandle } from "./add-item-search";
import { repaintPreservingScroll } from "@tkos/shared-ui";

// Verbatim from kos-qb-v3.js:3341 — three horizontal bars vs three-with-tail.
// Amber = SUMMARY mode active; muted = ITEMISED (default).
const SUMMARY_ICON_ON  = "M3 6h18|M3 12h18|M3 18h18";
const SUMMARY_ICON_OFF = "M3 6h13|M3 12h13|M3 18h13|M19 6h2|M19 12h2|M19 18h2";
// Right-arrow used by the line "Move to another zone" action (kos-qb-v3.js:3406).
const MOVE_ICON = "M5 12h14|M13 5l7 7-7 7";
// Duplicate / delete glyphs (kos-qb-v3.js:3405-3407).
const DUP_ICON = "M9 3h11v11H9z|M5 7h11v11H5z";
const DEL_ICON = "M3 6h18|M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6|M10 11v6|M14 11v6";
// Drag-handle grip (three bars) + pencil for the inline "Edit details" toggle.
const DRAG_ICON = "M5 9h14|M5 15h14";
const EDIT_ICON = "M12 20h9|M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z";

// ── Cross-render UI state ─────────────────────────────────────────────────
// Which lines have their inline spec editor expanded. Held outside the store
// (it's transient UI, never persisted) and outside render() so it survives
// the rebuild a field-edit triggers — otherwise the panel would snap shut on
// every keystroke-commit.
const expandedLines = new Set<string>();
// The line currently being dragged for reorder. Module-scoped so dragstart on
// one row and drop on another share it.
let draggingItemId: string | null = null;

// Reorder a line relative to a target line (used by drag-and-drop). Removes
// the dragged item from wherever it lives and re-inserts it before/after the
// target — which also handles a drag into a different zone.
function moveItemRelative(
  d: ReturnType<QuoteStore["get"]>,
  draggedId: string,
  targetId: string,
  after: boolean,
): void {
  let dragged: QuoteItem | undefined;
  for (const z of d.zones) {
    const i = z.items.findIndex((x) => x.id === draggedId);
    if (i >= 0) { [dragged] = z.items.splice(i, 1); break; }
  }
  if (!dragged) return;
  for (const z of d.zones) {
    const i = z.items.findIndex((x) => x.id === targetId);
    if (i >= 0) { z.items.splice(after ? i + 1 : i, 0, dragged); return; }
  }
  // Target vanished mid-drag — re-home the line rather than drop it.
  d.zones[0]?.items.push(dragged);
}

const CATEGORY_LABELS: Record<ItemCategory, string> = {
  cabinets: "Cabinet",
  doors: "Door",
  panels: "Panel",
  plinth: "Plinth",
  storage: "Storage",
  handles: "Handle",
  worktops: "Worktop",
  lighting: "Lighting",
  appliances: "Appliance",
  sundries: "Sundry",
  labour: "Labour",
};

// ── Meta strip ────────────────────────────────────────────────────────────

export function mountQuoteMeta(host: HTMLElement, store: QuoteStore, ctx: PageContext): () => void {
  let typeaheadCleanup: (() => void) | null = null;

  function render(): void {
    const q = store.get();
    host.innerHTML = renderMeta(q);
    wireMeta(host, store, render);

    typeaheadCleanup?.();
    typeaheadCleanup = null;
    const inputEl = host.querySelector<HTMLInputElement>("input[data-meta='customer']");
    if (inputEl) {
      const handle = mountCustomerTypeahead(inputEl, store, ctx);
      typeaheadCleanup = () => handle.destroy();
    }
  }

  render();
  const unsub = store.subscribe(() => {
    if (document.activeElement && host.contains(document.activeElement)) return;
    render();
  });
  return () => {
    typeaheadCleanup?.();
    unsub();
  };
}

/** One customer-facing note, shown as a readable card that opens a full-size
 *  editor. These were `rows="2"` textareas inline in the meta strip, which is
 *  fine for a sentence and unusable for the paragraph most covering notes
 *  actually are — you could neither write nor read one back. The card shows a
 *  preview so the note is legible at a glance without opening anything.
 *
 *  Plain text throughout, deliberately (Steve, 2026-08-19): the note is drawn
 *  into the customer PDF with a plain-text renderer, so any markup would need a
 *  rich-text PDF layout pass, sanitising, and matching customer-portal
 *  rendering before it could be offered. */
function noteCardHtml(field: string, label: string, hint: string, value: string): string {
  const text = value.trim();
  return `
    <div class="qb-meta__note-card${text ? "" : " qb-meta__note-card--empty"}" data-note-card="${field}">
      <div class="qb-meta__note-head">
        <span class="qb-meta__lbl">${escapeHtml(label)}</span>
        <button type="button" class="qb-meta__note-edit" data-note-edit="${field}">${text ? "Edit" : "Add"}</button>
      </div>
      <button type="button" class="qb-meta__note-preview" data-note-edit="${field}" title="${escapeHtml(hint)}">
        ${text ? escapeHtml(text) : `<span class="qb-meta__note-ph">${escapeHtml(hint)}</span>`}
      </button>
    </div>
  `;
}

function renderMeta(q: ReturnType<QuoteStore["get"]>): string {
  // CRM-linked badge mirrors source (kos-qb-v3.js:1448) — green tick when
  // `customerId` is set. The auto-CRM checkbox only appears when the user
  // has typed a name but no CRM record was selected; it's default-on, so
  // unchecking is an explicit opt-out.
  const linked = !!q.customerId;
  const showAutoCrm = !linked && (q.customer || "").trim().length > 0;
  return `
    <section class="qb-meta">
      <h3 class="qb-meta__title">Quote details</h3>
      <div class="qb-meta__grid">
        <label class="qb-meta__customer">
          <span class="qb-meta__lbl">
            Customer
            ${linked ? `<span class="qb-meta__crm-linked" title="Linked to CRM contact">${svgIcon(CHECK_ICON)} CRM linked</span>` : ""}
          </span>
          <input type="text" data-meta="customer" value="${escapeHtml(q.customer)}" placeholder="Search customer or type new name…" autocomplete="off" />
        </label>
        <label>Project  <input type="text" data-meta="project"  value="${escapeHtml(q.project)}"  placeholder="Project / job" /></label>
        <label>Reference <input type="text" data-meta="reference" value="${escapeHtml(q.reference)}" /></label>
        <label>Quote date <input type="date" data-meta="date" value="${escapeHtml(q.date)}" /></label>
        <label>VAT % <input type="number" data-meta="vatRate" min="0" max="100" step="0.5" value="${q.vatRate}" /></label>
        <label>Valid for (days) <input type="number" data-meta="validDays" min="1" step="1" value="${q.validDays}" /></label>
        <label>Markup % <input type="number" data-meta-defaults="markupPct" min="0" max="500" step="0.5" value="${q.defaults.markupPct ?? 0}" title="Default markup applied to new line items. Changing this only affects future adds — existing lines keep their stored markup." /></label>
        <fieldset class="qb-meta__group">
          <legend>Group by</legend>
          <label><input type="radio" name="qb-group" value="zone" ${q.groupBy === "zone" ? "checked" : ""}/> Zone</label>
          <label><input type="radio" name="qb-group" value="supplier" ${q.groupBy === "supplier" ? "checked" : ""}/> Supplier</label>
        </fieldset>
      </div>
      ${showAutoCrm ? `
        <label class="qb-meta__autocrm">
          <input type="checkbox" data-meta-autocrm checked />
          Save as new CRM contact when this quote is sent
        </label>
      ` : ""}
      <div class="qb-meta__notes">
        ${noteCardHtml("introNote", "Introduction / cover note", "Shown above the line items on the customer's quote", q.introNote ?? "")}
        ${noteCardHtml("closingNote", "Closing note", "Shown below the totals, above your terms", q.closingNote ?? "")}
      </div>
    </section>
  `;
}

// Inline tick used by the CRM-linked badge. Matches design-icons rule.
const CHECK_ICON = "M20 6L9 17l-5-5";

function wireMeta(host: HTMLElement, store: QuoteStore, rerender: () => void): void {
  host.querySelectorAll<HTMLInputElement>("[data-meta]").forEach((inp) => {
    inp.addEventListener("change", () => {
      const field = inp.dataset.meta as keyof ReturnType<QuoteStore["get"]>;
      const val = inp.type === "number" ? parseFloat(inp.value) || 0 : inp.value;
      store.set((d) => {
        (d as unknown as Record<string, unknown>)[field] = val;
      });
    });
  });
  // data-meta-defaults inputs write into the JSONB-backed defaults blob
  // rather than the top-level quote object. Used for quote-level seed
  // values like markupPct that ride along the existing defaults plumbing
  // (no new server column needed).
  host.querySelectorAll<HTMLInputElement>("[data-meta-defaults]").forEach((inp) => {
    inp.addEventListener("change", () => {
      const field = inp.dataset["metaDefaults"];
      if (!field) return;
      const val = inp.type === "number" ? parseFloat(inp.value) || 0 : inp.value;
      store.set((d) => {
        (d.defaults as unknown as Record<string, unknown>)[field] = val;
      });
    });
  });
  host.querySelectorAll<HTMLInputElement>("input[name='qb-group']").forEach((r) => {
    r.addEventListener("change", () => {
      if (!r.checked) return;
      store.set((d) => { d.groupBy = r.value as GroupBy; });
      rerender();
    });
  });
  // Intro / closing notes — free text persisted onto the quote (captured in
  // the snapshot at send). Edited in a modal rather than inline: see
  // noteCardHtml. Commit happens on Save so an abandoned edit changes nothing.
  host.querySelectorAll<HTMLElement>("[data-note-edit]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const field = btn.dataset["noteEdit"];
      if (field !== "introNote" && field !== "closingNote") return;
      openNoteEditor(store, field);
    });
  });
}

/** Full-size editor for a customer-facing note. */
function openNoteEditor(store: QuoteStore, field: "introNote" | "closingNote"): void {
  const isIntro = field === "introNote";
  const current = String(store.get()[field] ?? "");
  let ta: HTMLTextAreaElement | null = null;

  const modal = createModal({
    title: isIntro ? "Introduction / cover note" : "Closing note",
    subtitle: isIntro
      ? "Shown above the line items on the customer's quote."
      : "Shown below the totals, above your terms.",
    size: "lg",
    content: (body) => {
      body.innerHTML = `
        <textarea class="qb-note-editor" rows="16" spellcheck="true"
          placeholder="${isIntro
            ? "Introduce the quote — what you have designed, what is included, anything the customer should read first."
            : "Close the quote — next steps, lead times, how to accept."}"></textarea>
      `;
      ta = body.querySelector<HTMLTextAreaElement>(".qb-note-editor");
      if (ta) {
        // Assigned, not interpolated into the template: the note is plain text
        // and must never be parsed as markup on its way back into the editor.
        ta.value = current;
      }
    },
    onOpen: () => {
      // Caret at the end, so continuing an existing note needs no click.
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(ta.value.length, ta.value.length);
    },
    actions: [
      { label: "Cancel", variant: "ghost", closeOnClick: true },
      {
        label: "Save note",
        variant: "primary",
        closeOnClick: true,
        onClick: () => {
          const next = ta ? ta.value : current;
          if (next === current) return; // nothing typed — do not dirty the quote
          store.set((d) => { (d as unknown as Record<string, unknown>)[field] = next; });
        },
      },
    ],
    onClose: () => modal.destroy(),
  });
  modal.open();
}

// ── Items area (zones + delivery) ────────────────────────────────────────

export function mountQuoteItems(host: HTMLElement, store: QuoteStore, ctx: PageContext): () => void {
  // Item ids present at the previous render, so we can spot a freshly-added
  // line and flash it (item 7). null until the first paint so existing lines
  // never flash on load.
  let prevItemIds: Set<string> | null = null;
  // Add-item search boxes, one per zone footer. The items area rebuilds its
  // innerHTML on every render, so these must be torn down each time or their
  // listeners accumulate against detached nodes.
  let searchHandles: AddItemSearchHandle[] = [];

  // Pull any reasoning already captured on this quote so lines reopen with the
  // control in its filled state. Fire-and-forget + repaint: capture is
  // assistive, so it must never gate the first paint of the quote.
  let rationaleLoadedFor = "";
  function syncRationale(serverId: string | undefined): void {
    const key = serverId || "";
    if (!key || key === rationaleLoadedFor) return;
    rationaleLoadedFor = key;
    void loadForQuote(key).then(() => { render(); });
  }

  function render(): void {
    const q = store.get();
    syncRationale(q.serverId);
    // WRAPPED, because this rebuilds `host` wholesale and the items area is a
    // direct child of `.qb-right__scroll` — the RIGHT pane's only scroll
    // container. Emptying the host collapses its content height, the browser
    // clamps scrollTop to 0, and the quote renders from the top again. Every
    // edit to a line therefore threw Steve back to the head of the quote and
    // he re-scrolled to where he was working, once per edit.
    //
    // Identical cause to the left pane, reported 2026-09-07 and fixed then
    // with this same helper — see @tkos/shared-ui repaint-preserving-scroll.ts, whose header already
    // says the bug "was never SJB-specific". It was only ever wired into the
    // three pickers; the panel where the quote actually lives never got it.
    // The helper also puts focus and caret back, which the inline line edits
    // need for the same reason the colour filter did.
    repaintPreservingScroll(host, () => {
      for (const h of searchHandles) h.destroy();
      searchHandles = [];
      host.innerHTML = `
        ${renderZones(q)}
        ${renderDelivery(q)}
      `;
      wireZones(host, store, ctx, render);
      wireDelivery(host, store);
      host.querySelectorAll<HTMLElement>("[data-role='add-search']").forEach((slot) => {
        const zoneId = slot.dataset["zone"] ?? "";
        if (!zoneId) return;
        searchHandles.push(mountAddItemSearch(slot, store, zoneId, () => {
          render();
          // Re-render rebuilds this input, so put the caret back in the SAME
          // zone's box — adding several lines in a row should not need a click
          // between each one.
          host.querySelector<HTMLInputElement>(
            `[data-role='add-search'][data-zone="${CSS.escape(zoneId)}"] .qb-adds__input`,
          )?.focus();
        }));
      });
    });

    // Flash + scroll just-added lines so the retailer sees them land without
    // scrolling to the foot of a long quote.
    //
    // THE COUNT GUARD USED TO BE `=== 1`, WHICH EXCLUDED EVERY PICKER.
    // A picker does not commit a line, it commits a LINKED GROUP: an SJB
    // cabinet is the carcass, one frontal per leaf and a drilling line — two
    // to four rows for one unit, more on a multi-door larder. So the one path
    // that most needs this never took it, and Steve scrolled down by hand
    // after every single cabinet.
    //
    // The other cases the old comment named do not need a count to exclude
    // them: an edit, a delete and a drag-reorder all leave the id set alone,
    // so `added` is empty and nothing fires. Only a bulk zone-duplicate adds
    // many at once, and sending the panel to the first of those rows is the
    // right answer there too — it is where the duplicate landed.
    const currentIds = new Set<string>();
    for (const z of q.zones) for (const it of z.items) currentIds.add(it.id);
    if (prevItemIds) {
      const added = new Set([...currentIds].filter((id) => !prevItemIds!.has(id)));
      if (added.size) {
        const rows = [...host.querySelectorAll<HTMLElement>(".qb-line")]
          .filter((el) => !!el.dataset.item && added.has(el.dataset.item));
        // Scroll to the FIRST row of the group, so a four-line cabinet shows
        // from its carcass down rather than from its drilling line up.
        //
        // Guarded exactly as sjb-colour-combobox.ts:231 guards it, and for the
        // same two reasons: jsdom does not implement scrollIntoView, and
        // revealing the row is a nicety that must never break the quote. It
        // mattered less while the count guard was `=== 1`, because that never
        // fired under the harness at all.
        const first = rows[0];
        if (first && typeof first.scrollIntoView === "function") {
          first.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
        for (const li of rows) {
          li.classList.add("qb-line--flash");
          window.setTimeout(() => li.classList.remove("qb-line--flash"), 1400);
        }
      }
    }
    prevItemIds = currentIds;
  }
  render();
  const unsub = store.subscribe(() => {
    if (document.activeElement && host.contains(document.activeElement)) return;
    render();
  });
  return () => {
    for (const h of searchHandles) h.destroy();
    searchHandles = [];
    unsub();
  };
}

function renderZones(q: ReturnType<QuoteStore["get"]>): string {
  if (q.groupBy === "supplier") return renderBySupplier(q);
  const zoneBlocks = q.zones.map((z) => renderZoneBlock(z, q.zoneSummary[z.id] === true)).join("");
  return `
    <section class="qb-zones">
      ${zoneBlocks}
      <button type="button" class="qb-btn qb-btn--ghost" data-role="add-zone">+ Add zone / room</button>
    </section>
  `;
}

function renderZoneBlock(z: QuoteZone, summary: boolean): string {
  const subtotalPence = z.items.reduce((s, it) => s + pencify(it.retailPrice || 0) * (it.qty || 0), 0);
  // Count the total quantity across rows, not the number of rows — a line
  // "Base unit ×3" is three items, not one.
  const count = z.items.reduce((s, it) => s + (it.qty || 0), 0);
  return `
    <article class="qb-zone" data-zone="${escapeHtml(z.id)}">
      <header class="qb-zone__head">
        <input class="qb-zone__name" type="text" value="${escapeHtml(z.name)}" data-role="zone-name" data-zone="${escapeHtml(z.id)}" />
        <span class="qb-zone__count">${count} item${count === 1 ? "" : "s"} · ${fmtPence(subtotalPence)}</span>
        <div class="qb-zone__actions">
          <button type="button" class="qb-icon-btn ${summary ? "qb-icon-btn--amber" : ""}" data-role="zone-summary" data-zone="${escapeHtml(z.id)}" title="Customer PDF: ${summary ? "Currently SUMMARY — click to itemise" : "Currently ITEMISED — click to summarise"}">${svgIcon(summary ? SUMMARY_ICON_ON : SUMMARY_ICON_OFF)}</button>
          <button type="button" class="qb-icon-btn" data-role="zone-dup" data-zone="${escapeHtml(z.id)}" title="Duplicate zone">${svgIcon(DUP_ICON)}</button>
          <button type="button" class="qb-icon-btn qb-icon-btn--danger" data-role="zone-del" data-zone="${escapeHtml(z.id)}" title="Delete zone">${svgIcon(DEL_ICON)}</button>
        </div>
      </header>
      <input class="qb-zone__note" type="text" placeholder="Zone-wide note (e.g. all handles fitted on site)" value="${escapeHtml(z.note)}" data-role="zone-note" data-zone="${escapeHtml(z.id)}" />
      ${summary ? `
      <label class="qb-zone__summary">
        <span class="qb-zone__summary-label">Customer-PDF description (shown instead of the itemised list)</span>
        <textarea class="qb-zone__summary-input" rows="2" placeholder="e.g. Dust grey gloss handleless doors with oak cabinets" data-role="zone-summary-text" data-zone="${escapeHtml(z.id)}">${escapeHtml(z.summary)}</textarea>
      </label>` : ""}
      <ul class="qb-zone__lines">
        ${renderZoneLines(z)}
      </ul>
      <div class="qb-zone__addbar">
        <div class="qb-zone__search" data-role="add-search" data-zone="${escapeHtml(z.id)}"></div>
        <button type="button" class="qb-btn qb-btn--ghost qb-zone__add" data-role="add-custom" data-zone="${escapeHtml(z.id)}">+ Blank line</button>
      </div>
    </article>
  `;
}

/**
 * The door lines committed with a cabinet — read from the link the SJB
 * picker writes (`sjbGroupId`, with `sjbGroupHead` on the cabinet), never
 * from the "Frontal for 900TW" note text, which a retailer can edit.
 *
 * Doors only: a drilling charge or an end panel shares the group id too but
 * is not a door. Returns nothing when the cabinet is not the group's ONLY
 * head — a duplicated cabinet line copies the id, and two cabinets cannot
 * both own the same doors.
 */
function linkedDoors(head: QuoteItem, items: readonly QuoteItem[]): QuoteItem[] {
  const gid = head.sjbGroupId;
  if (!head.sjbGroupHead || !gid) return [];
  if (items.some((x) => x.id !== head.id && x.sjbGroupHead && x.sjbGroupId === gid)) return [];
  return items.filter((x) => x.id !== head.id && !x.sjbGroupHead
    && x.category === "doors" && x.sjbGroupId === gid);
}

/**
 * A zone's lines, with each cabinet and its doors inside one shared border
 * (Steve, 2026-10-10). Display only: the stored order is untouched, the doors
 * are drawn straight after their cabinet wherever they sit in the zone, and
 * every row is still an ordinary `.qb-line` so editing, drag and delete wire
 * exactly as before. The supplier view does not come through here — there a
 * cabinet and its doors are under different suppliers by definition.
 */
function renderZoneLines(z: QuoteZone): string {
  const groups = new Map<string, QuoteItem[]>();
  const grouped = new Set<string>();
  for (const it of z.items) {
    const doors = linkedDoors(it, z.items);
    if (!doors.length) continue;
    groups.set(it.id, doors);
    for (const d of doors) grouped.add(d.id);
  }
  return z.items.map((it) => {
    if (grouped.has(it.id)) return "";
    const doors = groups.get(it.id);
    if (!doors) return renderLine(it, z.id);
    return `<li class="qb-line-group" data-group="${escapeHtml(it.sjbGroupId ?? "")}">
        <ul class="qb-line-group__lines">${[it, ...doors].map((x) => renderLine(x, z.id)).join("")}</ul>
      </li>`;
  }).join("");
}

/** Three answers, so neither confirmDialog's two buttons nor a native confirm
 *  will do. Built on createModal like the shared dialogs; Escape or the
 *  backdrop is a Cancel. */
function askDeleteLinkedDoors(count: number): Promise<"both" | "cabinet" | "cancel"> {
  return new Promise((resolve) => {
    let result: "both" | "cabinet" | "cancel" = "cancel";
    const modal = createModal({
      title: "Delete cabinet",
      size: "sm",
      content: (body) => {
        const p = document.createElement("p");
        p.className = "tkos-modal__body-text";
        p.textContent = count === 1
          ? "This cabinet has a matching door. Delete the matching door too?"
          : `This cabinet has ${count} matching doors. Delete the matching doors too?`;
        body.appendChild(p);
      },
      actions: [
        { label: "Cancel", variant: "ghost", onClick: () => { result = "cancel"; } },
        { label: "Cabinet only", variant: "secondary", onClick: () => { result = "cabinet"; } },
        { label: "Delete both", variant: "danger", onClick: () => { result = "both"; } },
      ],
      onClose: () => {
        resolve(result);
        window.setTimeout(() => modal.destroy(), 300);
      },
    });
    modal.open();
  });
}

function renderBySupplier(q: ReturnType<QuoteStore["get"]>): string {
  const buckets = new Map<string, { items: Array<{ it: QuoteItem; zoneId: string }>; }>();
  for (const z of q.zones) {
    for (const it of z.items) {
      const k = it.supplier || "Manual";
      const slot = buckets.get(k) ?? { items: [] };
      slot.items.push({ it, zoneId: z.id });
      buckets.set(k, slot);
    }
  }
  // The line/zone controls must never disappear just because the view is
  // grouped by supplier (BUG 4). A new custom line has no supplier yet, so
  // it can't belong to a supplier bucket — drop it into the first zone (the
  // user can re-price/re-supplier it, after which it regroups). `add-zone`
  // and `add-custom` reuse the same handlers wired in wireZones().
  const firstZoneId = q.zones[0]?.id ?? "";
  return `<section class="qb-zones">${[...buckets.entries()]
    .map(([sup, b]) => {
      const subtotalPence = b.items.reduce((s, x) => s + pencify(x.it.retailPrice || 0) * (x.it.qty || 0), 0);
      // Total quantity across the supplier's rows, not the row count.
      const count = b.items.reduce((s, x) => s + (x.it.qty || 0), 0);
      return `
        <article class="qb-zone qb-zone--by-supplier">
          <header class="qb-zone__head">
            <span class="qb-zone__name">${escapeHtml(sup)}</span>
            <span class="qb-zone__count">${count} item${count === 1 ? "" : "s"} · ${fmtPence(subtotalPence)}</span>
          </header>
          <ul class="qb-zone__lines">${b.items.map((x) => renderLine(x.it, x.zoneId)).join("")}</ul>
        </article>
      `;
    })
    .join("")}
      <div class="qb-zones__add-bar">
        <div class="qb-zone__search" data-role="add-search" data-zone="${escapeHtml(firstZoneId)}"></div>
        <button type="button" class="qb-btn qb-btn--ghost" data-role="add-custom" data-zone="${escapeHtml(firstZoneId)}">+ Blank line</button>
        <button type="button" class="qb-btn qb-btn--ghost" data-role="add-zone">+ Add zone / room</button>
      </div>
    </section>`;
}

function renderLine(it: QuoteItem, zoneId: string): string {
  const linePence = pencify(it.retailPrice || 0) * (it.qty || 0);
  const expanded = expandedLines.has(it.id);
  return `
    <li class="qb-line${expanded ? " is-expanded" : ""}" data-item="${escapeHtml(it.id)}" data-zone="${escapeHtml(zoneId)}">
      <button type="button" class="qb-line__drag" data-role="drag-handle" title="Drag to reorder" aria-label="Drag to reorder">${svgIcon(DRAG_ICON)}</button>
      <div class="qb-line__main">
        <input class="qb-line__desc" type="text" value="${escapeHtml(it.desc)}" data-field="desc" placeholder="${escapeHtml(CATEGORY_LABELS[it.category])} description" />
        <div class="qb-line__spec">
          <span class="qb-line__cat">${escapeHtml(CATEGORY_LABELS[it.category])}</span>
          <span class="qb-line__sup">${escapeHtml(it.supplier)}</span>
          ${it.code ? `<span class="qb-line__code">${escapeHtml(it.code)}</span>` : ""}
          ${it.width ? `<span class="qb-line__dim">${it.width}W</span>` : ""}
          ${it.height ? `<span class="qb-line__dim">${it.height}H</span>` : ""}
          ${it.depth ? `<span class="qb-line__dim">${it.depth}D</span>` : ""}
          ${(it.carcaseColour || it.carcassFinish) ? `<span class="qb-line__colour">Cabinet: ${escapeHtml(it.carcaseColour || it.carcassFinish || "")}</span>` : ""}
          ${it.doorColour ? `<span class="qb-line__colour">Door: ${escapeHtml(it.doorColour)}</span>` : ""}
          ${/* An SJB panel's colour IS its board (it has no carcass), held on
              sjbBoardLabel — shown as a tag because in the one-line note it
              sat third and was always cut off. Edging falls back to
              sjbEdgingColour so panels added before it was copied to
              `edging` show it too. */ ""}
          ${it.category === "panels" && it.sjbBoardLabel && !(it.carcaseColour || it.carcassFinish) ? `<span class="qb-line__colour">Board: ${escapeHtml(it.sjbBoardLabel)}</span>` : ""}
          ${(it.edging || it.sjbEdgingColour) ? `<span class="qb-line__colour">Edging: ${escapeHtml(it.edging || it.sjbEdgingColour || "")}</span>` : ""}
          ${it.notes ? `<span class="qb-line__noterow">
            <span class="qb-line__note" data-role="line-note">Note: ${escapeHtml(it.notes)}</span>
            <button type="button" class="qb-line__note-more" data-action="note-toggle" aria-expanded="false" title="Show the full note">more</button>
          </span>` : ""}
        </div>
      </div>
      <div class="qb-line__qty">
        <label>Qty <input type="number" min="0" step="1" value="${it.qty}" data-field="qty" /></label>
      </div>
      <div class="qb-line__price">
        <label>Trade £ <input type="number" min="0" step="0.01" value="${(it.tradePrice || 0).toFixed(2)}" data-field="tradePrice" /></label>
        <label>Disc % <input type="number" min="0" max="100" step="0.5" value="${(it.discPct || 0).toFixed(1)}" data-field="discPct" /></label>
        <label>Retail £ <input type="number" min="0" step="0.01" value="${(it.retailPrice || 0).toFixed(2)}" data-field="retailPrice" /></label>
      </div>
      <div class="qb-line__total">
        <span>${fmtPence(linePence)}</span>
        <div class="qb-line__row-actions">
          ${renderWhyButton(it.id)}
          <button type="button" class="qb-icon-btn qb-icon-btn--mode" data-action="markup-toggle" title="Toggle £/% markup">${it.markupMode === "gbp" ? "£" : "%"}</button>
          <button type="button" class="qb-icon-btn ${expanded ? "qb-icon-btn--amber" : ""}" data-action="edit" title="Edit line details" aria-pressed="${expanded ? "true" : "false"}">${svgIcon(EDIT_ICON)}</button>
          <button type="button" class="qb-icon-btn" data-action="move" title="Move to another zone">${svgIcon(MOVE_ICON)}</button>
          <button type="button" class="qb-icon-btn" data-action="dup" title="Duplicate">${svgIcon(DUP_ICON)}</button>
          <button type="button" class="qb-icon-btn qb-icon-btn--danger" data-action="del" title="Remove">${svgIcon(DEL_ICON)}</button>
        </div>
      </div>
      ${expanded ? renderLineDetails(it) : ""}
      ${renderWhyEditor(it.id)}
    </li>
  `;
}

// Inline spec editor revealed by the row's pencil toggle — lets the retailer
// correct description/spec/price without reopening the picker (item 6). Every
// input carries a data-field, so the existing line-input change handler
// persists + recalculates them; no extra wiring needed.
//
// List £ and Markup live here, not on the collapsed row. The row shows Trade £,
// Disc % and Retail £ (Steve, 2026-09-28): Trade catches cost drift, Disc % is
// the lever and must be visible even at 0.0 so a MISSING discount reads as
// plainly as an applied one, Retail is the customer figure. List is only an
// input on the way to Trade, and Markup is derivable from the two figures on
// the row (and the Totals margin/markup toggle already shows it). Same
// data-field names, same handler, same recalc direction.
function renderLineDetails(it: QuoteItem): string {
  return `
    <div class="qb-line__details" data-role="line-details">
      <label class="qb-line__details-cost">List £<input type="number" min="0" step="0.01" data-field="listPrice" value="${(it.listPrice || 0).toFixed(2)}" /></label>
      <label class="qb-line__details-cost">${it.markupMode === "gbp" ? "Markup £" : "Markup %"}<input type="number" min="0" step="0.5" data-field="${it.markupMode === "gbp" ? "markupGbp" : "markupPct"}" value="${it.markupMode === "gbp" ? (it.markupGbp || 0).toFixed(2) : (it.markupPct || 0).toFixed(1)}" /></label>
      <label>Supplier<input type="text" data-field="supplier" value="${escapeHtml(it.supplier)}" /></label>
      <label>Code<input type="text" data-field="code" value="${escapeHtml(it.code || "")}" /></label>
      <label>Width (mm)<input type="number" min="0" step="1" data-field="width" value="${it.width || 0}" /></label>
      <label>Height (mm)<input type="number" min="0" step="1" data-field="height" value="${it.height || 0}" /></label>
      <label>Depth (mm)<input type="number" min="0" step="1" data-field="depth" value="${it.depth || 0}" /></label>
      <label>Cabinet finish<input type="text" data-field="carcassFinish" value="${escapeHtml(it.carcassFinish || it.carcaseColour || "")}" /></label>
      <label>Door colour<input type="text" data-field="doorColour" value="${escapeHtml(it.doorColour || "")}" /></label>
      <label>Edging<input type="text" data-field="edging" value="${escapeHtml(it.edging || it.sjbEdgingColour || "")}" /></label>
      <label class="qb-line__details-note">Note<input type="text" data-field="notes" value="${escapeHtml(it.notes || "")}" /></label>
    </div>
  `;
}

function renderDelivery(q: ReturnType<QuoteStore["get"]>): string {
  const gbp = (q.deliveryChargePence / 100).toFixed(2);
  return `
    <section class="qb-delivery">
      <label>Delivery charge £
        <input type="number" min="0" step="0.01" value="${gbp}" data-role="delivery" />
      </label>
      <p class="qb-delivery__hint">Included in customer total (ex-VAT before VAT is applied).</p>
    </section>
  `;
}

function wireZones(host: HTMLElement, store: QuoteStore, ctx: PageContext, rerender: () => void): void {
  host.querySelector<HTMLButtonElement>("[data-role='add-zone']")?.addEventListener("click", () => {
    store.set((d) => {
      d.zones.push({ id: newId("z"), name: `Zone ${d.zones.length + 1}`, note: "", summary: "", items: [] });
    });
    rerender();
  });

  host.querySelectorAll<HTMLInputElement>("[data-role='zone-name']").forEach((inp) => {
    inp.addEventListener("change", () => {
      const id = inp.dataset.zone ?? "";
      store.set((d) => {
        const z = d.zones.find((zo) => zo.id === id);
        if (z) z.name = inp.value;
      });
    });
  });
  host.querySelectorAll<HTMLInputElement>("[data-role='zone-note']").forEach((inp) => {
    inp.addEventListener("change", () => {
      const id = inp.dataset.zone ?? "";
      store.set((d) => {
        const z = d.zones.find((zo) => zo.id === id);
        if (z) z.note = inp.value;
      });
    });
  });
  host.querySelectorAll<HTMLTextAreaElement>("[data-role='zone-summary-text']").forEach((ta) => {
    ta.addEventListener("change", () => {
      const id = ta.dataset.zone ?? "";
      store.set((d) => {
        const z = d.zones.find((zo) => zo.id === id);
        if (z) z.summary = ta.value;
      });
    });
  });
  host.querySelectorAll<HTMLButtonElement>("[data-role='zone-summary']").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset.zone ?? "";
      let summary = false;
      store.set((d) => {
        d.zoneSummary[id] = !d.zoneSummary[id];
        summary = d.zoneSummary[id];
      });
      ctx.showToast(`Customer PDF for this zone: ${summary ? "SUMMARY" : "itemised"}`, "success");
      rerender();
    });
  });
  host.querySelectorAll<HTMLButtonElement>("[data-role='zone-dup']").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset.zone ?? "";
      store.set((d) => {
        const src = d.zones.find((zo) => zo.id === id);
        if (!src) return;
        const copy: QuoteZone = {
          id: newId("z"),
          name: `${src.name} (copy)`,
          note: src.note,
          summary: src.summary,
          items: src.items.map((it) => ({ ...it, id: newId("i") })),
        };
        d.zones.push(copy);
      });
      rerender();
    });
  });
  host.querySelectorAll<HTMLButtonElement>("[data-role='zone-del']").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset.zone ?? "";
      void (async () => {
        if (!(await confirmDialog("Delete this zone and all its items?", {
          title: "Delete zone",
          confirmLabel: "Delete",
          destructive: true,
        }))) return;
        store.set((d) => {
          d.zones = d.zones.filter((zo) => zo.id !== id);
          if (d.zones.length === 0) d.zones.push({ id: newId("z"), name: "Kitchen", note: "", summary: "", items: [] });
        });
        rerender();
      })();
    });
  });
  host.querySelectorAll<HTMLButtonElement>("[data-role='add-custom']").forEach((btn) => {
    btn.addEventListener("click", () => {
      const zoneId = btn.dataset.zone ?? "";
      store.set((d) => {
        const z = d.zones.find((zo) => zo.id === zoneId);
        if (!z) return;
        const item: QuoteItem = {
          id: newId("i"),
          category: "sundries",
          supplier: "Manual",
          code: "",
          desc: "Custom item",
          qty: 1, width: 0, height: 0, depth: 0,
          notes: "",
          listPrice: 0, discPct: 0, tradePrice: 0,
          markupMode: "pct", markupPct: store.get().defaults.markupPct ?? 0, markupGbp: 0,
          retailPrice: 0,
        };
        z.items.push(item);
      });
      rerender();
    });
  });

  // ── Line-level inputs
  host.querySelectorAll<HTMLLIElement>(".qb-line").forEach((li) => {
    const itemId = li.dataset.item ?? "";
    li.querySelectorAll<HTMLInputElement>("input[data-field]").forEach((inp) => {
      inp.addEventListener("change", () => {
        const field = inp.dataset.field as keyof QuoteItem;
        const isNumeric = ["qty", "width", "height", "depth", "listPrice", "discPct", "tradePrice", "markupPct", "markupGbp", "retailPrice"].includes(field);
        const value: string | number = isNumeric ? (parseFloat(inp.value) || 0) : inp.value;
        store.set((d) => {
          for (const z of d.zones) {
            const it = z.items.find((x) => x.id === itemId);
            if (it) {
              (it as unknown as Record<string, unknown>)[field] = value;
              if (["listPrice", "discPct", "tradePrice", "markupPct", "markupGbp", "retailPrice"].includes(field)) {
                recalcItem(it, field);
                // Apprentice Kit: a line priced by hand is exactly the moment
                // the reasoning is worth capturing. Nudge only — never blocks
                // the edit, and the note is optional. Checked AFTER recalc so
                // the back-solved markup is the value we compare.
                if (isAnomalousEdit(field, it, d.defaults.markupPct ?? 0)) markAnomaly(it.id);
              }
              break;
            }
          }
        });
        rerender();
      });
    });
    li.querySelectorAll<HTMLButtonElement>("[data-action]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const action = btn.dataset.action;
        if (action === "why") {
          // Opening the editor answers the nudge. Clicking the control while
          // a nudge is showing also counts as dismissing it — the retailer has
          // seen the prompt either way.
          const wasNudged = isNudged(itemId);
          toggleWhyEditor(itemId);
          if (wasNudged && !isWhyOpen(itemId)) dismissNudge(itemId);
          rerender();
          // Focus the box so it can be typed into without a second click.
          window.setTimeout(() => {
            host.querySelector<HTMLTextAreaElement>(
              `.qb-why-editor[data-item="${CSS.escape(itemId)}"] [data-role="why-input"]`,
            )?.focus();
          }, 0);
          return;
        }
        if (action === "note-toggle") {
          // Class flip, NOT a store write and NOT a rerender. Every other
          // branch here ends in rerender(), and repainting the panel from a
          // control that sits next to a text input is the exact shape of the
          // 2026-09-16 "highlights but does nothing" bug — the repaint lands
          // on the blur and eats the click. Nothing about an expanded note
          // needs to persist, so nothing here touches the quote.
          const noteEl = li.querySelector<HTMLElement>("[data-role='line-note']");
          if (!noteEl) return;
          const open = noteEl.classList.toggle("is-open");
          btn.textContent = open ? "less" : "more";
          btn.setAttribute("aria-expanded", open ? "true" : "false");
          btn.title = open ? "Collapse the note" : "Show the full note";
          return;
        }
        if (action === "dup") {
          store.set((d) => {
            for (const z of d.zones) {
              const idx = z.items.findIndex((x) => x.id === itemId);
              if (idx >= 0) {
                const src = z.items[idx]!;
                const copy: QuoteItem = { ...src, id: newId("i") };
                z.items.splice(idx + 1, 0, copy);
                break;
              }
            }
          });
        } else if (action === "del") {
          const all = store.get().zones.flatMap((z) => z.items);
          const target = all.find((x) => x.id === itemId);
          const doors = target ? linkedDoors(target, all) : [];
          if (doors.length) {
            void (async () => {
              const pick = await askDeleteLinkedDoors(doors.length);
              if (pick === "cancel") return;
              const drop = new Set([itemId, ...(pick === "both" ? doors.map((x) => x.id) : [])]);
              for (const id of drop) expandedLines.delete(id);
              store.set((d) => {
                for (const z of d.zones) z.items = z.items.filter((x) => !drop.has(x.id));
              });
              rerender();
            })();
            return;
          }
          expandedLines.delete(itemId);
          store.set((d) => {
            for (const z of d.zones) z.items = z.items.filter((x) => x.id !== itemId);
          });
        } else if (action === "edit") {
          // Toggle the inline spec editor for this line. Transient UI state —
          // tracked outside the store so a field commit doesn't collapse it.
          if (expandedLines.has(itemId)) expandedLines.delete(itemId);
          else expandedLines.add(itemId);
        } else if (action === "markup-toggle") {
          // Verbatim port of _qbv3LineMarkupToggle (kos-qb-v3.js:4772-4776).
          store.set((d) => {
            for (const z of d.zones) {
              const it = z.items.find((x) => x.id === itemId);
              if (it) {
                it.markupMode = it.markupMode === "gbp" ? "pct" : "gbp";
                break;
              }
            }
          });
        } else if (action === "move") {
          // Verbatim port of _qbv3LineMove (kos-qb-v3.js:4793-4807). Single
          // prompt — drag-to-reorder is a separate deferred feature.
          void (async () => {
            const q = store.get();
            const current = q.zones.find((z) => z.items.some((x) => x.id === itemId));
            if (!current) return;
            const others = q.zones.filter((z) => z.id !== current.id);
            if (others.length === 0) {
              ctx.showToast("Add another zone first", "error");
              return;
            }
            const names = others.map((z, i) => `${i + 1}. ${z.name}`).join("\n");
            const pick = await promptDialog(`Move to which zone?\n${names}\n\nEnter number:`, {
              title: "Move line",
              confirmLabel: "Move",
            });
            if (!pick) return;
            const dest = others[parseInt(pick, 10) - 1];
            if (!dest) {
              ctx.showToast("Invalid zone", "error");
              return;
            }
            store.set((d) => {
              const src = d.zones.find((z) => z.id === current.id);
              const tgt = d.zones.find((z) => z.id === dest.id);
              if (!src || !tgt) return;
              const idx = src.items.findIndex((x) => x.id === itemId);
              if (idx < 0) return;
              const [moved] = src.items.splice(idx, 1);
              if (moved) tgt.items.push(moved);
            });
            ctx.showToast(`Moved to ${dest.name}`, "success");
            rerender();
          })();
          return;
        }
        rerender();
      });
    });

    // ── Reasoning editor (Apprentice Kit capture)
    const whyEd = li.querySelector<HTMLElement>("[data-role='why-editor']");
    if (whyEd) {
      const input = whyEd.querySelector<HTMLTextAreaElement>("[data-role='why-input']");
      const statusEl = whyEd.querySelector<HTMLElement>("[data-role='why-status']");
      const saveBtn = whyEd.querySelector<HTMLButtonElement>("[data-role='why-save']");

      const commit = async (): Promise<void> => {
        const text = (input?.value ?? "").trim();
        if (!text) { closeWhyEditor(); rerender(); return; }
        const q = store.get();
        let zoneId = "";
        let item: QuoteItem | undefined;
        for (const z of q.zones) {
          const found = z.items.find((x) => x.id === itemId);
          if (found) { zoneId = z.id; item = found; break; }
        }
        if (!item) { closeWhyEditor(); rerender(); return; }
        if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = "Saving…"; }
        const ok = await saveRationale(text, {
          quoteId: q.serverId,
          quoteRef: q.reference,
          zoneId,
          item,
          // A note typed in response to the pulse is an answer to a prompt;
          // one typed unprompted is a volunteered remark. Kit weighs them
          // differently, so the distinction is recorded at capture time.
          source: wasPrompted(itemId) ? "anomaly" : "manual",
        });
        if (!ok) {
          // Keep the editor open and the text intact — losing what was just
          // typed is far worse than an unsaved note.
          if (statusEl) statusEl.textContent = "Couldn't save — try again.";
          if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = "Save note"; }
          return;
        }
        closeWhyEditor();
        rerender();
        ctx.showToast("Reasoning saved", "success");
      };

      saveBtn?.addEventListener("click", () => { void commit(); });
      whyEd.querySelector<HTMLButtonElement>("[data-role='why-cancel']")
        ?.addEventListener("click", () => { closeWhyEditor(); rerender(); });
      // Cmd/Ctrl+Enter saves; Escape closes without saving. Enter alone must
      // insert a newline — this is prose, not a single-line field.
      input?.addEventListener("keydown", (e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void commit(); }
        else if (e.key === "Escape") { e.preventDefault(); closeWhyEditor(); rerender(); }
      });
    }
  });

  // ── Drag-to-reorder (item 5)
  // Native HTML5 DnD, initiated only from the grip so the line's inputs stay
  // selectable. Dropping before/after a target re-sequences the order list to
  // match the design layout; dropping onto a line in another zone moves it
  // there (a pointer-driven equivalent of the "move" action).
  const clearDropMarks = (): void => {
    host.querySelectorAll(".qb-line--drop-before, .qb-line--drop-after")
      .forEach((el) => el.classList.remove("qb-line--drop-before", "qb-line--drop-after"));
  };
  host.querySelectorAll<HTMLLIElement>(".qb-line").forEach((li) => {
    const handle = li.querySelector<HTMLElement>("[data-role='drag-handle']");
    if (!handle) return;
    // The li is only draggable while the grip is held — otherwise dragging a
    // text selection inside an input would start a row drag.
    handle.addEventListener("mousedown", () => { li.draggable = true; });
    // A grip click that never became a drag must not leave the row draggable
    // (which would let an input text-selection start a drag). dragend covers
    // the real-drag case; this covers click-without-drag.
    handle.addEventListener("mouseup", () => { li.draggable = false; });
    li.addEventListener("dragstart", (e) => {
      draggingItemId = li.dataset.item ?? null;
      li.classList.add("is-dragging");
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", draggingItemId ?? "");
      }
    });
    li.addEventListener("dragend", () => {
      li.classList.remove("is-dragging");
      li.draggable = false;
      draggingItemId = null;
      clearDropMarks();
    });
    li.addEventListener("dragover", (e) => {
      if (!draggingItemId || li.dataset.item === draggingItemId) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
      const rect = li.getBoundingClientRect();
      const after = e.clientY > rect.top + rect.height / 2;
      li.classList.toggle("qb-line--drop-after", after);
      li.classList.toggle("qb-line--drop-before", !after);
    });
    li.addEventListener("dragleave", () => {
      li.classList.remove("qb-line--drop-before", "qb-line--drop-after");
    });
    li.addEventListener("drop", (e) => {
      if (!draggingItemId) return;
      e.preventDefault();
      const targetId = li.dataset.item ?? "";
      const dragged = draggingItemId;
      const rect = li.getBoundingClientRect();
      const after = e.clientY > rect.top + rect.height / 2;
      clearDropMarks();
      if (targetId && targetId !== dragged) {
        store.set((d) => moveItemRelative(d, dragged, targetId, after));
        rerender();
      }
    });
  });
}

function wireDelivery(host: HTMLElement, store: QuoteStore): void {
  host.querySelector<HTMLInputElement>("[data-role='delivery']")?.addEventListener("change", (e) => {
    const v = parseFloat((e.target as HTMLInputElement).value) || 0;
    store.set((d) => { d.deliveryChargePence = pencify(v); });
  });
}
